import { z } from "zod";

/** The site-fields round (2026-10-29; brief docs/briefs/site-fields-round.md, rule 1; DECISIONS "2026-10-29 — Site fields both ways"):
 *  a *site field* is one named extra value a connected site keeps beside a forum post (kriesi.at's "Private data", the post meta
 *  `_av_supp_private_info`), carried with the message as `{ key, label, value }`; the *reply fields* are the fields a team reply may carry
 *  back, declared by the site when it files the topic, as `{ key, label }`. OmnaBase knows nothing of what a field means: up to ten labelled
 *  plain texts per message. Pure. */

/** at most this many fields per message, and reply fields per ticket (the plugin's settings and the pop-up share the bound) */
export const SITE_FIELDS_MAX = 10;
/** a value's length as sent */
export const SITE_FIELD_VALUE_MAX = 10_000;

/** a site's meta key, lower-cased first, then 1–100 characters of a-z 0-9 _ - */
const FieldKey = z.string().trim().toLowerCase().pipe(z.string().min(1).max(100).regex(/^[a-z0-9_-]+$/, "Letters a-z, digits, _ and -"));
const FieldLabel = z.string().trim().min(1).max(60);
/** a value as plain text: tags out (as a note's text), line breaks kept, trimmed; may come out empty (the list drops it) */
const FieldValue = z.string().max(SITE_FIELD_VALUE_MAX).transform(plainFieldValue);

/** a value's words without HTML tags: every tag removed, the line breaks the site sent kept, the ends trimmed */
export function plainFieldValue(value: string): string {
  return value.replace(/<[^>]*>/g, "").replace(/\r\n?/g, "\n").trim();
}

/** each key once (after lower-casing), checked on the parsed list */
const keysOnce = (items: { key: string }[], ctx: z.RefinementCtx) => {
  const seen = new Set<string>();
  items.forEach((f, i) => { if (seen.has(f.key)) ctx.addIssue({ code: "custom", message: `The field ${f.key} is named twice`, path: [i, "key"] }); seen.add(f.key); });
};
/** the items whose value came out empty dropped (not refused) */
const withValues = <F extends { value: string }>(list: F[]): F[] => list.filter((f) => f.value !== "");

/** a site's field on a post it sends (`POST /tickets`, `POST /tickets/{n}/messages`): its own label */
export const SiteFieldSchema = z.object({ key: FieldKey, label: FieldLabel, value: FieldValue }).strict();
/** at most ten as sent, each key once, empty values dropped */
export const SiteFieldsSchema = z.array(SiteFieldSchema).max(SITE_FIELDS_MAX).superRefine(keysOnce).transform(withValues);
/** the fields a team reply may carry back (`POST /tickets`'s `replyFields`): names only, never a value */
export const ReplyFieldSchema = z.object({ key: FieldKey, label: FieldLabel }).strict();
export const ReplyFieldsSchema = z.array(ReplyFieldSchema).max(SITE_FIELDS_MAX).superRefine(keysOnce);
/** the team's reply (`MessageCreateSchema`'s `fields`): a declared key and its value; the label is the site's, added by the server */
export const ReplyFieldValueSchema = z.object({ key: FieldKey, value: FieldValue }).strict();
export const ReplyFieldValuesSchema = z.array(ReplyFieldValueSchema).max(SITE_FIELDS_MAX).superRefine(keysOnce).transform(withValues);

/** the answers: a message's fields as the team's read shows them, a ticket's reply fields */
export const SiteFieldOutSchema = z.object({ key: z.string(), label: z.string(), value: z.string() });
export const ReplyFieldOutSchema = z.object({ key: z.string(), label: z.string() });
export type SiteField = z.infer<typeof SiteFieldOutSchema>;
export type ReplyField = z.infer<typeof ReplyFieldOutSchema>;

/** the stored jsonb read back: only well-formed items, anything else left out (the column is ours, so this is a guard, not a parser) */
export function siteFieldsOf(raw: unknown): SiteField[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((f): f is SiteField => !!f && typeof f === "object" && typeof f.key === "string" && typeof f.label === "string" && typeof f.value === "string");
}
export function replyFieldsOf(raw: unknown): ReplyField[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((f): f is ReplyField => !!f && typeof f === "object" && typeof f.key === "string" && typeof f.label === "string").map((f) => ({ key: f.key, label: f.label }));
}

/** The team's reply's values joined with the ticket's declaration: each kept key gets the declared label; an undeclared key is a refusal
 *  naming it ("This site takes no field <key>"). Pure: the route turns the refusal into a 400. */
export function declaredReplyFields(values: { key: string; value: string }[], declared: ReplyField[]): { fields: SiteField[] } | { refused: string } {
  const labels = new Map(declared.map((d) => [d.key, d.label]));
  const unknown = values.find((v) => !labels.has(v.key));
  if (unknown) return { refused: `This site takes no field ${unknown.key}` };
  return { fields: values.map((v) => ({ key: v.key, label: labels.get(v.key)!, value: v.value })) };
}
